import Edit from '@mui/icons-material/Edit'
import { LoadingButton } from '@mui/lab'
import { Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { FieldModel } from '@cibo/core'
import { useSaveDetail } from '../../../.'

export const FieldNameEditor = ({ field }: { field: FieldModel }) => {
  const { t } = useTranslation('FieldNameEditor')
  const [showEditor, setShowEditor] = useState(false)
  const toggleEditor = useCallback(() => setShowEditor((show: boolean) => !show), [])
  const fieldEditor = useSaveDetail()
  const handleSave = ({ name }: { name: string }) => {
    fieldEditor.mutateAsync(
      {
        resourceId: field.resourceId,
        details: [{ traitId: 'name', input: { name } }],
      },
      {
        onSuccess: toggleEditor,
      }
    )
  }

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(t('nameRequired')).max(255, t('nameMax')),
      }),
    [t]
  )

  return showEditor ? (
    <Stack direction="row">
      <Formik
        initialValues={{ name: field.name }}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSave}
      >
        {props => (
          <Form style={{ flex: 1 }}>
            <Stack spacing={1}>
              <Field
                id="name"
                name="name"
                component={TextField}
                InputProps={{ inputProps: { 'data-testid': 'existingFieldName' } }}
                maxLength={255}
                placeholder={t('placeholder')}
                disabled={fieldEditor.isLoading}
                fullWidth={true}
              />

              <Stack direction="row" spacing={2}>
                <Button variant="text" onClick={toggleEditor} fullWidth>
                  {t('cancel')}
                </Button>
                <LoadingButton
                  loading={fieldEditor.isLoading}
                  variant="contained"
                  disabled={!props.dirty}
                  type="submit"
                  fullWidth
                >
                  {t('save')}
                </LoadingButton>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  ) : (
    <Stack direction="row" justifyContent="space-between" spacing={1}>
      <Tooltip title={field.name} arrow>
        <Typography
          variant="h5"
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {field.name}
        </Typography>
      </Tooltip>
      <Tooltip title={`${t('edit')}`}>
        <IconButton size="small" onClick={toggleEditor} aria-label={t('edit')}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
